import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from "@/components/ui/sheet";
import { Menu, ArrowRight } from "lucide-react";
import { NAV_LINKS, TOP_NAV_LINKS, scrollToId } from "./nav";

export default function Header({ scrolled }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => {
      const sections = NAV_LINKS.map((l) => document.getElementById(l.id)).filter(Boolean);
      const y = window.scrollY + 140;
      let current = "home";
      for (const s of sections) {
        if (s.offsetTop <= y) current = s.id;
      }
      setActive(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="container-x flex items-center justify-between">
        <button
          onClick={() => handleClick("home")}
          className="flex items-center gap-2 group"
          data-testid="header-logo"
        >
          <div className="relative flex items-center gap-2">
            <div className="w-9 h-9 bg-[#0A192F] flex items-center justify-center">
              <span className="font-display text-white font-bold text-lg leading-none">Б</span>
              <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-[#FF6B00]" />
            </div>
            <div className="hidden sm:block leading-tight text-left">
              <div className={`font-display font-semibold text-sm tracking-tight transition-colors ${scrolled ? "text-[#0A192F]" : "text-white"}`}>
                Бизора Логистик
              </div>
              <div className={`text-[10px] uppercase tracking-[0.2em] transition-colors ${scrolled ? "text-slate-500" : "text-white/60"}`}>
                B2B поставки
              </div>
            </div>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-8">
          {TOP_NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => handleClick(l.id)}
              data-testid={`nav-link-${l.id}`}
              className={`hover-underline text-sm font-medium transition-colors whitespace-nowrap ${
                active === l.id
                  ? "text-[#FF6B00]"
                  : scrolled
                    ? "text-slate-700 hover:text-[#0A192F]"
                    : "text-white/80 hover:text-white"
              }`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="tel:+79223829669"
            data-testid="header-phone"
            className={`hidden md:inline-flex items-center text-sm font-medium hover-underline transition-colors ${scrolled ? "text-[#0A192F]" : "text-white"}`}
          >
            +7 922 382-96-69
          </a>
          <button
            onClick={() => handleClick("request")}
            data-testid="header-cta"
            className="hidden sm:inline-flex items-center gap-2 bg-[#FF6B00] hover:bg-[#E66000] text-white px-5 py-3 text-sm font-medium transition-colors group"
          >
            Заявка
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                className={`lg:hidden inline-flex items-center justify-center w-10 h-10 border transition-colors ${scrolled ? "border-slate-300 text-[#0A192F]" : "border-white/30 text-white"}`}
                aria-label="Открыть меню"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-96 bg-white">
              <SheetHeader>
                <SheetTitle className="font-display text-[#0A192F] text-left">
                  Меню
                </SheetTitle>
              </SheetHeader>
              <div className="mt-8 flex flex-col gap-1" data-testid="mobile-nav">
                {NAV_LINKS.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => handleClick(l.id)}
                    data-testid={`mobile-nav-${l.id}`}
                    className="text-left py-4 border-b border-slate-100 font-display text-lg text-[#0A192F] hover:text-[#FF6B00] transition-colors"
                  >
                    {l.label}
                  </button>
                ))}
                <button
                  onClick={() => handleClick("request")}
                  className="mt-6 bg-[#FF6B00] hover:bg-[#E66000] text-white py-4 font-medium transition-colors"
                  data-testid="mobile-cta"
                >
                  Оставить заявку
                </button>
                <a
                  href="tel:+79223829669"
                  className="mt-4 text-center text-[#0A192F] font-medium"
                >
                  +7 922 382-96-69
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
